import { ChevronLeft, ChevronRight } from 'lucide-react'
import { PAGE_SIZE_OPTIONS, type PaginationMeta } from '../../lib/api-list'
import { cn } from '../../lib/utils'
import { Button } from './button'

type ListPaginationProps = {
  meta: PaginationMeta | null
  perPage: number
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  disabled?: boolean
}

function pageWindow(current: number, last: number): number[] {
  if (last <= 5) {
    return Array.from({ length: last }, (_, i) => i + 1)
  }

  const start = Math.max(1, Math.min(current - 1, last - 4))
  const end = Math.min(last, start + 4)
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

export function ListPagination({
  meta,
  perPage,
  onPageChange,
  onPerPageChange,
  disabled = false,
}: ListPaginationProps) {
  if (!meta || meta.total === 0) {
    return null
  }

  const from = (meta.current_page - 1) * meta.per_page + 1
  const to = Math.min(meta.current_page * meta.per_page, meta.total)
  const pages = pageWindow(meta.current_page, meta.last_page)
  const canPrev = meta.current_page > 1
  const canNext = meta.current_page < meta.last_page

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-parch-100 bg-white shadow-card">
      <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="inline-flex items-center gap-2 text-sm text-ink-600">
            <span className="text-xs font-medium tracking-wide text-ink-500 uppercase">Show</span>
            <select
              className="h-9 min-w-[4.5rem] rounded-lg border border-parch-200 bg-parch-50 px-2.5 text-sm font-semibold text-ink-900 outline-none transition-colors hover:bg-white focus:border-green-600 focus:bg-white focus:ring-2 focus:ring-green-600/20 disabled:opacity-50"
              value={perPage}
              disabled={disabled}
              aria-label="Records Per Page"
              onChange={(event) => onPerPageChange(Number(event.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-ink-500">per page</span>
          </label>

          <span className="hidden h-5 w-px bg-parch-200 sm:block" aria-hidden />

          <p className="text-sm text-ink-600">
            Showing{' '}
            <span className="font-semibold tabular-nums text-ink-900">
              {from}–{to}
            </span>{' '}
            of <span className="font-semibold tabular-nums text-ink-900">{meta.total}</span>
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            size="iconSm"
            variant="secondary"
            className="rounded-lg border-parch-200"
            disabled={disabled || !canPrev}
            aria-label="Previous Page"
            onClick={() => onPageChange(Math.max(1, meta.current_page - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {pages[0] > 1 && (
            <>
              <PageButton
                page={1}
                active={meta.current_page === 1}
                disabled={disabled}
                onClick={onPageChange}
              />
              {pages[0] > 2 && (
                <span className="px-1 text-xs text-ink-400" aria-hidden>
                  …
                </span>
              )}
            </>
          )}

          {pages.map((page) => (
            <PageButton
              key={page}
              page={page}
              active={page === meta.current_page}
              disabled={disabled}
              onClick={onPageChange}
            />
          ))}

          {pages[pages.length - 1] < meta.last_page && (
            <>
              {pages[pages.length - 1] < meta.last_page - 1 && (
                <span className="px-1 text-xs text-ink-400" aria-hidden>
                  …
                </span>
              )}
              <PageButton
                page={meta.last_page}
                active={meta.current_page === meta.last_page}
                disabled={disabled}
                onClick={onPageChange}
              />
            </>
          )}

          <Button
            size="iconSm"
            variant="secondary"
            className="rounded-lg border-parch-200"
            disabled={disabled || !canNext}
            aria-label="Next Page"
            onClick={() => onPageChange(meta.current_page + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function PageButton({
  page,
  active,
  disabled,
  onClick,
}: {
  page: number
  active: boolean
  disabled?: boolean
  onClick: (page: number) => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-current={active ? 'page' : undefined}
      aria-label={`Page ${page}`}
      onClick={() => onClick(page)}
      className={cn(
        'inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-sm font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600/20 disabled:pointer-events-none disabled:opacity-50',
        active
          ? 'bg-green-600 text-white shadow-sm'
          : 'text-ink-700 hover:bg-parch-50',
      )}
    >
      {page}
    </button>
  )
}
